import { useEffect, useState, useRef, useMemo } from 'react';
import { Loader2, Search, ChevronDown, X, Building } from 'lucide-react';

interface Site {
  sid: number;
  name: string;
}

interface Props {
  selectedSid: number | null;
  onSelect: (sid: number, name: string) => void;
}

export function SiteSelector({ selectedSid, onSelect }: Props) {
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/sites');
        if (!res.ok) throw new Error('Failed to load sites');
        const data: Site[] = await res.json();
        setSites(data.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return sites;
    const q = query.toLowerCase();
    return sites.filter(
      (s) => s.name.toLowerCase().includes(q) || String(s.sid).includes(q)
    );
  }, [sites, query]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [filtered]);

  useEffect(() => {
    if (open && listRef.current) {
      const highlighted = listRef.current.children[highlightIndex] as HTMLElement;
      highlighted?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex, open]);

  const selectedSite = sites.find((s) => s.sid === selectedSid);

  function handleSelect(site: Site) {
    onSelect(site.sid, site.name);
    setQuery('');
    setOpen(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filtered[highlightIndex]) handleSelect(filtered[highlightIndex]);
        break;
      case 'Escape':
        setOpen(false);
        break;
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 py-2">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-[14px]">Loading merchants...</span>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
        Merchant
      </label>

      {!open ? (
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-left hover:border-slate-300 transition-colors"
        >
          {selectedSite ? (
            <>
              <Building className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span className="flex-1 text-[14px] text-slate-900 truncate">
                {selectedSite.name}
              </span>
              <span className="text-[12px] text-slate-400 flex-shrink-0">
                SID {selectedSite.sid}
              </span>
            </>
          ) : (
            <>
              <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span className="flex-1 text-[14px] text-slate-400">
                Search {sites.length.toLocaleString()} merchants...
              </span>
            </>
          )}
          <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
        </button>
      ) : (
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="w-4 h-4 text-slate-400" />
          </div>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type to search by name or SID..."
            className="w-full pl-9 pr-9"
            autoFocus
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <X className="w-4 h-4 text-slate-400 hover:text-slate-600" />
            </button>
          )}
        </div>
      )}

      {open && (
        <ul
          ref={listRef}
          className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg"
        >
          {filtered.length === 0 ? (
            <li className="px-4 py-6 text-center text-[14px] text-slate-500">
              No merchants matching "{query}"
            </li>
          ) : (
            filtered.map((site, idx) => (
              <li
                key={site.sid}
                onClick={() => handleSelect(site)}
                onMouseEnter={() => setHighlightIndex(idx)}
                className={`flex items-center gap-2 px-3 py-2.5 cursor-pointer transition-colors ${
                  idx === highlightIndex ? 'bg-violet-50' : ''
                } ${site.sid === selectedSid ? 'bg-violet-50/50' : ''}`}
              >
                <Building className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <span className="flex-1 text-[14px] text-slate-900 truncate">
                  {site.name}
                </span>
                <span className="text-[12px] text-slate-400 flex-shrink-0">
                  SID {site.sid}
                </span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
